/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { Grid, Form, Button, Icon, Modal } from '@mairistem/composants';
import {
  EtatCivil,
  SectionHeader,
  TypeComponent,
} from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { ChampLieu, ChampProfession } from '@/components/Common';
import { OPTIONS_SEXE_ETCV, filiationInconnueAutorisee } from '@/constants';
import {
  buildIndividuPutPayload,
  buildParentUpdatePayload,
  estDecede,
  estInconnu,
  parseDateNaissance,
  parseIndividuFiche,
} from '@/utils';
import { validationInformationsNaissance } from '@/utils/validations';

interface ModalParentProps {
  /**
   * Endpoint générique des individus d'acte. Quand il est fourni,
   * l'enregistrement passe par `PUT {urlIndividus}/{id}` au lieu du PUT du
   * dossier de numérisation (qui n'existe pas pour tous les types d'acte).
   */
  urlIndividus?: string;
  /**
   * Affiche la section « Autre information » et son champ Profession (défaut
   * `true`, comportement naissance). `false` en reconnaissance : décision
   * produit, la profession n'y est ni saisie ni affichée sur la fiche.
   */
  showProfession?: boolean;
  /**
   * Case « Parent décédé » dans l'en-tête de l'état civil (défaut `true`,
   * AD-12923). `false` en reconnaissance, où la case de l'assistant dit
   * « parent inconnu » et non « décédé ».
   */
  caseDecede?: boolean;
  /**
   * Case « Parent inconnu » (défaut `false`, AD-12923) : reconnaissance
   * seulement, et pour le seul parent que l'auteur de la reconnaissance laisse
   * inconnu, comme dans l'assistant (`filiationInconnueAutorisee`).
   */
  caseInconnu?: boolean;
  /**
   * Parent décédé d'un acte de DÉCÈS (AD-13025) : lieu de naissance et
   * profession grisés et vidés quand « Parent décédé » est coché, comme dans
   * l'assistant. Défaut `false`.
   */
  griserSiDecede?: boolean;
  /** L'acte : porte l'auteur de la reconnaissance. */
  acte?: any;
  open: boolean;
  /** Numéro du parent (1 ou 2) : la modal est la même, seule la clé du payload change. */
  numeroParent: 1 | 2;
  /**
   * Libellés avec article (« le père » / « la mère ») quand le type d'acte
   * impose des rôles, cf. `constants/rolesParentsParTypeActe`. Absent en
   * naissance : on retombe sur « le parent N ».
   */
  libellesParents?: Record<1 | 2, string>;
  /**
   * Verrouille la civilité, déduite du rôle en reconnaissance. Cohérent avec
   * l'assistant, qui la préremplit et la grise également.
   */
  disabledSexe?: boolean;
  /** Identifiant de la fiche (celui de l'acte), utilisé pour l'URL de `service.update`. */
  identifiant?: number;
  parent?: any;
  /** Date de naissance de l'enfant, pour le contrôle « le parent doit être né avant l'enfant ». */
  naissanceDateEnfant?: string;
  /** Instance `service` de `useFiche` fournie par `ActeFicheContainer` (celle de la lecture). */
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (parent: any) => void;
  /**
   * Options du champ « Sexe » (défaut : Masculin / Féminin).
   * `OPTIONS_SEXE_ETCV_AVEC_INDETERMINE` là où « Indéterminé » est prévu.
   */
  optionsSexe?: typeof OPTIONS_SEXE_ETCV;
}

/**
 * État civil écrit pour un parent inconnu : vide, comme l'individu créé par
 * l'assistant et comme l'ETCV (pas de drapeau « inconnu » en base). Le nom de
 * classement suit, recalculé et recopié sur l'acte par l'API.
 */
const ETAT_CIVIL_INCONNU = {
  nomFamille: '',
  nomUsage: '',
  prenom: '',
  prenomsComplementaires: '',
  naissanceDate: null,
  naissanceDepartementCode: '',
  naissanceDepartementLibelle: '',
  naissanceCodePostal: '',
  naissanceLocalite: '',
  naissancePays: '',
};

/** Lieu de naissance et profession d'un parent décédé d'un décès (AD-13025). */
const LIEU_ET_PROFESSION_VIDES = {
  naissanceDepartementCode: '',
  naissanceDepartementLibelle: '',
  naissanceCodePostal: '',
  naissanceLocalite: '',
  naissancePays: '',
  profession: '',
};

const CHAMPS_EDITABLES = [
  'nomFamille',
  'nomUsage',
  'prenom',
  'prenomsComplementaires',
  'sexe',
  'jourNaissance',
  'moisNaissance',
  'anneeNaissance',
  'adresseNaissance',
  'profession',
];

/**
 * `EtatCivil` est utilisé sans `attributePrefix` (contrairement au `ParentForm`
 * de la lib, qui émet des noms préfixés `parentN.`) : `data` porte donc les
 * champs à plat, ce qui permet de réutiliser le `handleChange` maison ET
 * `validationInformationsNaissance` telle quelle.
 */
const ModalParent = (props: ModalParentProps) => {
  const {
    open,
    numeroParent,
    libellesParents,
    disabledSexe = false,
    urlIndividus,
    showProfession = true,
    caseDecede = true,
    caseInconnu = false,
    griserSiDecede = false,
    acte,
    identifiant,
    parent,
    naissanceDateEnfant,
    service,
    onClose,
    onSaved,
    optionsSexe = OPTIONS_SEXE_ETCV,
  } = props;

  /** « le père », « la mère »… ou le rang si le type d'acte n'impose rien. */
  const roleParent =
    libellesParents?.[numeroParent] ?? `le parent ${numeroParent}`;

  const inconnuPossible =
    caseInconnu &&
    filiationInconnueAutorisee(numeroParent, acte?.codeAuteurReconnaissance);

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      ..._.pick(parseIndividuFiche(parent), CHAMPS_EDITABLES),
      ...(caseDecede && { decede: estDecede(parent) }),
      ...(inconnuPossible && { inconnu: estInconnu(parent) }),
    });
    setAttempted(false);
  }, [open, parent, caseDecede, inconnuPossible]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  /** validateur attend annee + mois + jour, sinon erreur (date incomplète) */
  const dateNaissanceEnfant = React.useMemo(() => {
    const triplet = parseDateNaissance(naissanceDateEnfant);
    return _.isNil(triplet.anneeNaissance)
      ? undefined
      : (triplet as {
          anneeNaissance: number;
          moisNaissance: number;
          jourNaissance: number;
        });
  }, [naissanceDateEnfant]);

  // Un parent inconnu n'a rien à saisir, donc rien à contrôler.
  const errors = React.useMemo(
    () =>
      data?.inconnu
        ? {}
        : validationInformationsNaissance(
            data,
            true,
            // Parent décédé d'un décès : aucun contrôle, comme dans l'assistant
            // (AD-13025).
            griserSiDecede && Boolean(data?.decede),
            dateNaissanceEnfant,
          ),
    [data, dateNaissanceEnfant, griserSiDecede],
  );

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    // Cf. ModalEnfant : `urlIndividus` (reconnaissance) passe par l'endpoint
    // générique de l'individu ; sinon (naissance) par le PUT du dossier, qui
    // recalcule `ai_sexel` et alimente le référentiel des professions.
    const sansObjet = griserSiDecede && Boolean(data?.decede);
    const payload = buildParentUpdatePayload(
      identifiant as number,
      numeroParent,
      parent,
      data,
    );
    // Parent inconnu : son état civil est vidé, comme à la création ; parent
    // décédé d'un décès : son lieu de naissance et sa profession.
    const vidage = {
      ...(sansObjet && LIEU_ET_PROFESSION_VIDES),
      ...(data?.inconnu && ETAT_CIVIL_INCONNU),
    };
    Object.assign(payload[`parent${numeroParent}`], vidage);
    const enregistrement = urlIndividus
      ? request.put(`${urlIndividus}/${parent?.identifiant}`, {
          ...buildIndividuPutPayload(parent, data),
          ...vidage,
        })
      : service?.update(payload);

    // Ni endpoint d'individu, ni service : rien à quoi envoyer la modification.
    if (!enregistrement) {
      return;
    }

    setSaving(true);
    enregistrement
      .then(() => {
        onSaved({
          ...parent,
          ...payload[`parent${numeroParent}`],
          ...(caseDecede && { decede: data.decede }),
        });
        onClose();
      })
      .catch(() => {
        toast.error(
          `Une erreur est survenue lors de l'enregistrement de ${roleParent}`,
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    errors,
    service,
    identifiant,
    numeroParent,
    parent,
    urlIndividus,
    data,
    roleParent,
    caseDecede,
    griserSiDecede,
    onSaved,
    onClose,
  ]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content={`Modifier ${roleParent}`} />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <EtatCivil
            isParent
            title="État civil"
            typeComponent={TypeComponent.NAISSANCE}
            optionsSexe={optionsSexe}
            disabledSexe={disabledSexe}
            individu={data}
            errors={displayedErrors}
            onChange={handleChange}
            /*
             * « Parent inconnu » : la case native de la lib pour un parent de
             * reconnaissance, qui grise l'état civil quand elle est cochée.
             */
            {...(inconnuPossible && {
              typeComponent: TypeComponent.RECONNAISSANCE,
              isFiche: true,
              filiationInconnue: Boolean(data?.inconnu),
              onFiliationInconnueChange: (inconnu: boolean) =>
                handleChange(null, { name: 'inconnu', value: inconnu }),
            })}
            {...(caseDecede && {
              libelleCaseDecede: 'Parent décédé',
              filiationInconnue: Boolean(data?.decede),
              onFiliationInconnueChange: (decede: boolean) => {
                handleChange(null, { name: 'decede', value: decede });
                // Décès : ce qui devient sans objet est vidé (AD-13025).
                if (griserSiDecede && decede) {
                  handleChange(null, { name: 'adresseNaissance', value: {} });
                  handleChange(null, { name: 'profession', value: '' });
                }
              },
            })}
          />
          <ChampLieu
            data={data}
            errors={{ adresseNaissance: displayedErrors.adresseNaissance }}
            onChange={handleChange}
            disabled={
              Boolean(data?.inconnu) ||
              (griserSiDecede && Boolean(data?.decede))
            }
          />
          {showProfession && (
            <>
              {/* @ts-ignore */}
              <SectionHeader title="Divers" />
              <Grid>
                <Grid.Row style={{ paddingLeft: '1em' }}>
                  <Grid.Column computer={8} tablet={16} mobile={16}>
                    <ChampProfession
                      name="profession"
                      value={data?.profession}
                      disabled={griserSiDecede && Boolean(data?.decede)}
                      onChange={handleChange}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalParent;
